import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Filter, X } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/site/Reveal";
import { getProjects } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const INDUSTRIES = ["E-commerce", "FinTech", "Healthcare", "SaaS", "Education", "Manufacturing", "Retail", "Logistics"];
const TECHS = ["React", "Next.js", "Vue.js", "Flutter", "React Native", "Node.js", "Python", "Go", "AWS", "Azure", "GCP", "Kubernetes"];
const SERVICES = ["Web Application", "Mobile App", "Cloud", "AI/ML", "DevOps", "UI/UX Design"];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [industryF, setIndustryF] = useState([]);
  const [techF, setTechF] = useState([]);
  const [serviceF, setServiceF] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProjects().then((data) => {
      setProjects(data);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    return projects.filter((p) => {
      const indOk = industryF.length === 0 || industryF.some((f) => p.industry.includes(f));
      const techOk = techF.length === 0 || techF.some((f) => p.technologies.includes(f));
      const svcOk = serviceF.length === 0 || serviceF.some((f) => p.services.includes(f));
      return indOk && techOk && svcOk;
    });
  }, [projects, industryF, techF, serviceF]);

  const toggle = (val, list, setList) => {
    setList(list.includes(val) ? list.filter((v) => v !== val) : [...list, val]);
  };

  const clearAll = () => {
    setIndustryF([]);
    setTechF([]);
    setServiceF([]);
  };

  const active = [...industryF, ...techF, ...serviceF];

  return (
    <div data-testid="projects-page" className="pt-32 pb-20">
      <section className="relative mx-auto max-w-7xl px-6 md:px-12 mesh-bg pb-12">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">
            <Link to="/" className="hover:text-foreground">Home</Link> · Projects
          </p>
          <h1 className="mt-5 font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Our work <span className="text-gradient">speaks</span> for itself.
          </h1>
          <p className="mt-7 text-xl text-zinc-400 max-w-2xl">
            200+ projects across 15 industries. A selection of the work we're most proud of.
          </p>
        </Reveal>
      </section>

      {/* Filter Bar */}
      <section className="sticky top-20 z-30 mx-auto max-w-7xl px-6 md:px-12 mb-8">
        <div className="rounded-2xl glass-strong border-white/[0.08] p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06]">
              <Filter className="h-3.5 w-3.5 text-nexus-cyan" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Filter</span>
            </div>
            <FilterGroup label="Industry" options={INDUSTRIES} selected={industryF} onToggle={(v) => toggle(v, industryF, setIndustryF)} testid="industry" />
            <FilterGroup label="Tech" options={TECHS} selected={techF} onToggle={(v) => toggle(v, techF, setTechF)} testid="tech" />
            <FilterGroup label="Service" options={SERVICES} selected={serviceF} onToggle={(v) => toggle(v, serviceF, setServiceF)} testid="service" />
            <div className="ml-auto flex items-center gap-3">
              <span className="text-xs text-zinc-500" data-testid="projects-count">
                Showing <span className="text-foreground font-mono">{filtered.length}</span> of {projects.length}
              </span>
              {active.length > 0 && (
                <button data-testid="clear-filters" onClick={clearAll} className="text-xs text-zinc-400 hover:text-foreground inline-flex items-center gap-1">
                  Clear all <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          {active.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2 px-1">
              {active.map((f) => (
                <button
                  key={f}
                  onClick={() => {
                    if (industryF.includes(f)) setIndustryF(industryF.filter((v) => v !== f));
                    if (techF.includes(f)) setTechF(techF.filter((v) => v !== f));
                    if (serviceF.includes(f)) setServiceF(serviceF.filter((v) => v !== f));
                  }}
                  data-testid={`active-filter-${f}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-nexus-cyan/10 text-nexus-cyan border border-nexus-cyan/30 px-3 py-1 text-xs"
                >
                  {f} <X className="h-3 w-3" />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-7xl px-6 md:px-12">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="aspect-[4/5] rounded-3xl glass border-white/[0.08] animate-pulse" />
            ))}
          </div>
        ) : (
          <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {filtered.map((p, i) => (
                <Item key={p.id}>
                  <motion.div layout>
                    <Link
                      to={`/projects/${p.slug}`}
                      data-testid={`project-card-${p.slug}`}
                      className="group relative block rounded-3xl overflow-hidden border border-white/[0.08]"
                    >
                      <div className={`relative ${i % 5 === 0 ? "aspect-[4/5]" : i % 4 === 0 ? "aspect-[5/6]" : "aspect-[4/5]"} bg-zinc-900`}>
                        <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-nexus-ink via-nexus-ink/40 to-transparent" />
                        <div className="absolute inset-0 p-6 flex flex-col justify-between">
                          <div className="flex flex-wrap gap-2">
                            {p.industry.slice(0, 2).map((ind) => (
                              <Badge key={ind} variant="outline" className="rounded-full bg-white/5 text-zinc-200 border-white/10 text-[10px] uppercase tracking-wider">
                                {ind}
                              </Badge>
                            ))}
                          </div>
                          <div>
                            <h3 className="font-display text-2xl md:text-3xl tracking-tight">{p.title}</h3>
                            <p className="text-zinc-400 text-sm mt-2 line-clamp-2">{p.summary}</p>
                            <div className="mt-4 flex items-center justify-between">
                              <div className="flex flex-wrap gap-1.5">
                                {p.technologies.slice(0, 3).map((t) => (
                                  <span key={t} className="font-mono text-[10px] uppercase tracking-wider text-zinc-400 bg-white/5 border border-white/10 rounded-full px-2 py-0.5">
                                    {t}
                                  </span>
                                ))}
                              </div>
                              <ArrowUpRight className="h-5 w-5 text-nexus-cyan opacity-0 group-hover:opacity-100 transition" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                </Item>
              ))}
            </AnimatePresence>
          </Stagger>
        )}

        {!loading && filtered.length === 0 && (
          <div className="rounded-3xl glass border-white/[0.08] p-16 text-center">
            <p className="text-zinc-400">No projects match your filters.</p>
            <button onClick={clearAll} className="mt-4 rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-5 py-2 text-sm">
              Clear filters
            </button>
          </div>
        )}
      </section>

      <section className="mx-auto max-w-5xl px-6 md:px-12 mt-32">
        <div className="rounded-[2rem] glass border-white/[0.08] p-12 md:p-16 text-center">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight">
            Want similar results for your business?
          </h2>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto">
            Tell us about your project. We'll respond within one business day.
          </p>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-7 py-4 text-sm hover:brightness-110 transition glow-cyan">
            Start a project <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function FilterGroup({ label, options, selected, onToggle, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid={`filter-${testid}-toggle`}
        onClick={() => setOpen((s) => !s)}
        className="rounded-full px-4 py-1.5 text-xs font-medium border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition"
      >
        {label} {selected.length > 0 && <span className="text-nexus-cyan">({selected.length})</span>}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            data-testid={`filter-${testid}-menu`}
            className="absolute z-40 top-full mt-2 left-0 w-64 rounded-2xl glass-strong border-white/[0.08] p-2 max-h-72 overflow-auto"
            onMouseLeave={() => setOpen(false)}
          >
            {options.map((o) => (
              <button
                key={o}
                onClick={() => onToggle(o)}
                data-testid={`filter-${testid}-option-${o}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-white/[0.05] flex items-center justify-between ${selected.includes(o) ? "text-nexus-cyan" : "text-zinc-300"}`}
              >
                {o}
                {selected.includes(o) && <span className="text-xs">✓</span>}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
